import React, { useEffect, useState } from 'react';
import { DateField } from '@gerdau/hefesto-mais-web/DateField';
import { DateRangeType } from '@gerdau/hefesto-mais-web/DateField/DateField.types';
import { Button, Title, Select } from '@gerdau/hefesto';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import IsManagerCustomerInput from '../../components/IsManagerCustomerInput';
import { ApplicationState } from '../../store';
import { ICustomer } from '../../store/ducks/customers/types';
import {
  filterDuplicatesByDate,
  loadDuplicates
} from '../../store/ducks/invoices/actions';
import { sendTagging } from '../../store/ducks/tagging/actions';
import { ECustomerTypeView } from '../../store/ducks/user/types';
import { uppercaseOnlyFirstLetter } from '../../utils/formatStrings';
import FinancialsTabs from './FinancialsTabs';
import { CustomersContainer, FinancialsHeader } from './styles';
import SubHeader from './SubHeader';

export enum invoiceStatusType {
  ABERTO = 'V',
  PAGA = 'C',
  TODOS = 'A'
}

enum maxDateRange {
  PAGA = 3,
  TODOS = 1
}

const Financials: React.FC = () => {
  const dispatch = useDispatch();
  const creditLimitsState = useSelector(
    (state: ApplicationState) => state.creditLimits
  );

  const customerViewType = useSelector(
    (state: ApplicationState) => state.user.searchCustomerType
  );
  const customerType: string =
    customerViewType === ECustomerTypeView.EMITTER ? 'emissor' : 'recebedor';

  const customersState = useSelector(
    (state: ApplicationState) => state.customers
  );
  const customers = useSelector((state: ApplicationState) =>
    state.user.isManager
      ? state.customers.customers
      : state.customers.customers.filter((customer) =>
          state.user.roles.financialCustomers.includes(customer.customerId)
        )
  );

  const invoicesState = useSelector(
    (state: ApplicationState) => state.invoices
  );

  const invoicesCreditsState = useSelector(
    (state: ApplicationState) => state.invoices.invoiceCreditsState
  );
  const isManager = useSelector(
    (state: ApplicationState) => state.user.isManager
  );

  const isLoadingInvoices = useSelector(
    (state: ApplicationState) =>
      state.invoices.loading ||
      state.invoices.invoiceCreditsState.loading ||
      state.creditLimits.loading
  );

  const [selectedCustomerId, setSelectedCustomerId] = useState(
    invoicesState.initialInputValues?.selectedCustomerId
  );

  const [dateRange, setDateRange] = useState<DateRangeType>([
    invoicesState.initialInputValues?.startDate?.toDate(),
    invoicesState.initialInputValues?.endDate?.toDate()
  ]);

  const [selectedStatus, setSelectedStatus] = useState<string | undefined>();

  const [hasSearched, setHasSearched] = useState(false);

  const reloadInvoices = () => {
    loadFinancials();
  };

  useEffect(() => {
    if (
      !customersState.loading &&
      !customersState.error &&
      customers.length > 0 &&
      invoicesState.lastLoadTime
    ) {
      if (new Date().getTime() - invoicesState.lastLoadTime > 3600000) {
        // recarrega se tiver passado UMA HORA desde o último carregamento
        reloadInvoices();
      }
    }
  });

  useEffect(() => {
    if (customersState.customers.length) {
      dispatch(
        sendTagging({
          page: document.location.pathname,
          screenName: 'LG:Financas:MinhasFinancas',
          event: 'page_view'
        })
      );
    }
  }, [customersState.customers, dispatch]);

  const getSelectedCustomer = (customerId: string) => {
    return customers.find((cust) => cust.customerId === customerId);
  };

  const onSelectStatus = (status: string): void => {
    if (
      status === invoiceStatusType.TODOS &&
      moment(dateRange?.[1]).diff(moment(dateRange?.[0]), 'days') > 30
    ) {
      setDateRange([moment().subtract(1, 'month').toDate(), moment().toDate()]);
    }
    setHasSearched(false);
    setSelectedStatus(status);
  };

  const loadFinancials = () => {
    dispatch(
      sendTagging({
        page: document.location.pathname,
        screenName: 'LG:Financas:MinhasFinancas',
        event: 'buscar-minhasfinancas',
        itemName: 'button:buscar',
        itemDescription: 'financas_minhasfinancas_buscar',
        filterInfo: `#est:${
          selectedStatus === invoiceStatusType.ABERTO ? 'abertas' : 'pagas'
        }#codEmr:${selectedCustomerId ?? 'not_set'}#stsFat:not_set#dtInicio:${
          dateRange?.[0] ? moment(dateRange[0]).format('yyyy_MM_DD') : 'not_set'
        }#dtFim:${
          dateRange?.[1] ? moment(dateRange[1]).format('yyyy_MM_DD') : 'not_set'
        }`,
        type: 'click_filter'
      })
    );

    if (selectedCustomerId === undefined) {
      return;
    }

    const formatedCustomerId =
      isManager && selectedCustomerId.length < 10
        ? `0${selectedCustomerId}`
        : selectedCustomerId;

    if (
      selectedStatus === invoiceStatusType.ABERTO &&
      hasSearched &&
      formatedCustomerId === invoicesState.initialInputValues.selectedCustomerId
    ) {
      dispatch(
        filterDuplicatesByDate(moment(dateRange?.[0]), moment(dateRange?.[1]))
      );
    } else {
      const needToLoadCustomer =
        isManager &&
        !customersState.customers?.find(
          (c) => c.customerId === selectedCustomerId
        );

      setSelectedCustomerId(formatedCustomerId);

      dispatch(
        loadDuplicates(
          formatedCustomerId,
          customerViewType,
          moment(dateRange?.[0]),
          moment(dateRange?.[1]),
          needToLoadCustomer,
          selectedStatus
        )
      );
    }

    setHasSearched(true);
  };

  const renderStatusOptions = () => {
    return [
      { label: 'Abertas', value: invoiceStatusType.ABERTO },
      { label: 'Pagas', value: invoiceStatusType.PAGA },
      { label: 'Todos', value: invoiceStatusType.TODOS }
    ];
  };

  const shouldDisableDatePicker = () => {
    return (
      selectedCustomerId.length === 0 ||
      selectedStatus === undefined ||
      isLoadingInvoices ||
      selectedStatus === invoiceStatusType.ABERTO
    );
  };

  const renderHelperText = () => {
    let maxRange = 0;
    if (selectedStatus === invoiceStatusType.PAGA) {
      maxRange = maxDateRange.PAGA;
    } else if (selectedStatus === invoiceStatusType.TODOS) {
      maxRange = maxDateRange.TODOS;
    } else {
      return '';
    }
    return `A diferença entre as datas não pode ser maior que ${maxRange} meses`;
  };

  const datePickerMaxDate = () => {
    if (dateRange?.[0]) {
      if (selectedStatus === invoiceStatusType.PAGA) {
        return moment(dateRange?.[0]).add(maxDateRange.PAGA, 'months').toDate();
      } else if (selectedStatus === invoiceStatusType.TODOS) {
        return moment(dateRange?.[0])
          .add(maxDateRange.TODOS, 'months')
          .toDate();
      }
    }
  };

  const datePickerMinDate = () => {
    if (dateRange?.[0]) {
      if (selectedStatus === invoiceStatusType.PAGA) {
        return moment(dateRange?.[0])
          .subtract(maxDateRange.PAGA, 'months')
          .toDate();
      } else if (selectedStatus === invoiceStatusType.TODOS) {
        return moment(dateRange?.[0])
          .subtract(maxDateRange.TODOS, 'months')
          .toDate();
      }
    }
    if (dateRange?.[1]) {
      if (selectedStatus === invoiceStatusType.PAGA) {
        return moment(dateRange?.[1])
          .subtract(maxDateRange.PAGA, 'months')
          .toDate();
      } else if (selectedStatus === invoiceStatusType.TODOS) {
        return moment(dateRange?.[1])
          .subtract(maxDateRange.TODOS, 'months')
          .toDate();
      }
    }
  };

  const EmissorSelectOptions = (customers: ICustomer[]) =>
    customers.map((customer) => {
      return {
        label: `${uppercaseOnlyFirstLetter(customer.name)}`,
        value: `${customer.customerId}`,
        descriptions: [
          `BP: ${customer.customerId}`,
          `CNPJ: ${customer.taxNumber}`,
          `${uppercaseOnlyFirstLetter(
            customer.locality.address
          )} | ${uppercaseOnlyFirstLetter(customer.locality.city)} - ${
            customer.locality.state
          } | CEP ${customer.locality.zipCode}`
        ]
      };
    });

  const handleDateChange = (date: DateRangeType) => {
    if (date && date[0] && date[1]) {
      if (
        moment(date[0]).isAfter(moment(date[1])) ||
        moment(date[1]).isBefore(moment(date[0]))
      ) {
        setDateRange([date[0], null]);
        return;
      }

      if (selectedStatus === invoiceStatusType.PAGA) {
        if (
          moment(dateRange?.[0]).isBefore(
            moment(dateRange?.[1])
              .subtract(maxDateRange.PAGA, 'months')
              .toDate()
          )
        ) {
          setDateRange([date[0], null]);
          return;
        }
      }

      if (selectedStatus === invoiceStatusType.TODOS) {
        if (
          moment(dateRange?.[0]).isBefore(
            moment(dateRange?.[1])
              .subtract(maxDateRange.TODOS, 'months')
              .toDate()
          )
        ) {
          setDateRange([date[0], null]);
          return;
        }
      }
    }

    setDateRange(date);
  };

  const disableButton = () => {
    if (!selectedCustomerId || !selectedStatus || isLoadingInvoices) {
      return true;
    }
    if (
      selectedStatus === invoiceStatusType.PAGA ||
      selectedStatus === invoiceStatusType.TODOS
    ) {
      if (dateRange && moment(dateRange[0]).isAfter(moment(), 'day')) {
        return true;
      }
      if (!dateRange || dateRange.includes(null)) {
        return true;
      }
    }

    return false;
  };

  return (
    <>
      <FinancialsHeader>
        <Title level={1}>Minhas finanças</Title>

        <span className="subtitle">
          Selecione o {customerType} e as datas para exibir os resultados.
        </span>

        <CustomersContainer>
          <div className="input-container">
            {isManager && (
              <IsManagerCustomerInput
                title="Emissor do pedido"
                customerId={selectedCustomerId || ''}
                setCustomerId={setSelectedCustomerId}
                loading={isLoadingInvoices}
                bpError={invoicesState.bpError}
                customerName={
                  !isLoadingInvoices &&
                  customers.length > 0 &&
                  selectedCustomerId &&
                  selectedCustomerId.length > 6
                    ? uppercaseOnlyFirstLetter(
                        getSelectedCustomer(selectedCustomerId)?.name || '',
                        35
                      )
                    : undefined
                }
              />
            )}

            {!isManager && (
              <Select
                label={uppercaseOnlyFirstLetter(customerType)}
                disabled={customersState.loading || isLoadingInvoices}
                name="emissorSelect"
                options={EmissorSelectOptions(customers)}
                placeholder={`- Selecione -`}
                onSelect={(customerId) => {
                  setHasSearched(false);
                  setSelectedCustomerId(customerId as string);
                }}
                value={selectedCustomerId}
              ></Select>
            )}
          </div>

          <div className="input-status">
            <Select
              label={'Status das faturas'}
              disabled={customersState.loading || isLoadingInvoices}
              name="invoicesStatus"
              options={renderStatusOptions()}
              placeholder={`- Selecione -`}
              onSelect={(selectStatus) => {
                if (selectStatus === invoiceStatusType.ABERTO) {
                  if (!dateRange || !dateRange[0] || !dateRange[1]) {
                    setDateRange([
                      invoicesState.initialInputValues?.startDate?.toDate(),
                      invoicesState.initialInputValues?.endDate?.toDate()
                    ]);
                  }
                }
                onSelectStatus(selectStatus as string);
              }}
              value={selectedStatus}
            ></Select>
          </div>

          <div className="input-container">
            <DateField
              label="Período"
              name="dateField"
              selectsRange={true}
              helperText={renderHelperText()}
              onChange={handleDateChange}
              disabled={shouldDisableDatePicker()}
              maxDate={datePickerMaxDate()}
              minDate={datePickerMinDate()}
              data-testid="dateRange"
              range={dateRange}
              allowPartialRange={true}
            />
          </div>

          <div className="actions">
            <Button
              name="buscar"
              variant="primary"
              onClick={loadFinancials}
              disabled={disableButton()}
              loading={isLoadingInvoices || customersState.loading}
            >
              Buscar
            </Button>
          </div>
        </CustomersContainer>
      </FinancialsHeader>

      {invoicesState.invoices && (
        <SubHeader
          selectedCustomer={getSelectedCustomer(
            invoicesState.initialInputValues?.selectedCustomerId
          )}
          invoicesState={invoicesState}
          creditLimitsState={creditLimitsState}
          invoicesCreditsState={invoicesCreditsState}
          customersState={customersState}
          customers={customers}
        />
      )}

      {invoicesState.invoices && <FinancialsTabs />}
    </>
  );
};

export default Financials;
